import React, { useState, useEffect } from 'react';
import { 
  Box, FormControl, InputLabel, Select, MenuItem, Typography, 
  Alert, CircularProgress, Checkbox, FormControlLabel, Chip
} from '@mui/material';
import Plot from 'react-plotly.js';
import axios from 'axios';

const ComparePiers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPiers, setSelectedPiers] = useState([1, 2]);
  const [selectedDataType, setSelectedDataType] = useState('status'); // 'status' or 'strain'
  const [selectedStrain, setSelectedStrain] = useState('Strain(1)');
  const [pierData, setPierData] = useState({}); // { pier1: { timestamps: [], status: [], strains: {} }, ... }
  
  const pierColors = {
    1: '#1f77b4', // Blue
    2: '#ff7f0e', // Orange
    3: '#2ca02c'  // Green
  };

  // Load data for selected piers
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const dataPromises = selectedPiers.map(async (pier) => {
          try {
            const response = await axios.get(`/api/plot-data?pier=${pier}`);
            if (response.data.error) {
              throw new Error(response.data.error);
            }
            return { pier, data: response.data };
          } catch (err) {
            console.error(`Error loading data for pier ${pier}:`, err);
            return { pier, data: null, error: err.message };
          }
        });

        const results = await Promise.all(dataPromises);
        const newPierData = {};
        
        results.forEach(({ pier, data, error }) => {
          if (data) {
            newPierData[pier] = {
              timestamps: data.timestamps || [],
              status: data.status || [],
              strains: data.strains || {}
            };
          } else {
            newPierData[pier] = { error };
          }
        });
        
        setPierData(newPierData);
      } catch (err) {
        setError(`Error loading data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (selectedPiers.length > 0) {
      loadData();
    }
  }, [selectedPiers]);

  const convertTimestampsToDate = (timestamps) => {
    if (!timestamps || timestamps.length === 0) return [];
    
    return timestamps.map(ts => {
      let timestamp = ts;
      if (ts < 4102444800) {
        timestamp = ts * 1000;
      } else if (ts > 4102444800000) {
        const testDate = new Date(ts);
        if (testDate.getFullYear() < 1970 || testDate.getFullYear() > 2050) {
          timestamp = ts / 1000;
        } else {
          timestamp = ts;
        }
      }
      return new Date(timestamp);
    });
  };

  const getPlotData = () => {
    const plotData = [];
    
    if (selectedDataType === 'status') {
      selectedPiers.forEach(pier => {
        const data = pierData[pier];
        if (data && data.timestamps && data.status && data.status.length > 0) {
          plotData.push({
            type: 'scattergl',
            mode: 'lines+markers',
            x: convertTimestampsToDate(data.timestamps),
            y: data.status,
            line: { color: pierColors[pier], width: 2 },
            marker: { size: 4, color: pierColors[pier] },
            name: `Pier ${pier} Status`
          });
        }
      });
    } else {
      // Strain data
      selectedPiers.forEach(pier => {
        const data = pierData[pier];
        if (data && data.strains && data.strains[selectedStrain] && data.strains[selectedStrain].length > 0) {
          plotData.push({
            type: 'scattergl',
            mode: 'lines+markers',
            x: convertTimestampsToDate(data.timestamps),
            y: data.strains[selectedStrain],
            line: { color: pierColors[pier], width: 2 },
            marker: { size: 4, color: pierColors[pier] },
            name: `Pier ${pier} ${selectedStrain}`
          });
        }
      });
    }
    
    return plotData;
  };

  const plotData = getPlotData();

  const plotConfig = {
    data: plotData,
    layout: {
      title: selectedDataType === 'status' 
        ? 'Status Comparison Across Piers'
        : `${selectedStrain} Comparison Across Piers`,
      xaxis: { 
        title: 'Timestamp (Date)',
        type: 'date',
        tickangle: -45,
        autorange: true
      },
      yaxis: { 
        title: selectedDataType === 'status' 
          ? 'Status'
          : `Strain (Microstrain) - ${selectedStrain}`,
        autorange: true
      },
      autosize: true,
      width: 1200,
      height: 600,
      margin: { l: 80, r: 40, t: 60, b: 100 },
      showlegend: true,
      legend: { x: 0, y: 1.1, orientation: 'h' }
    },
    config: {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d']
    }
  };

  // Get available strain types from any pier
  const getAvailableStrains = () => {
    for (const pier of selectedPiers) {
      const data = pierData[pier];
      if (data && data.strains) {
        return Object.keys(data.strains).filter(key => 
          data.strains[key] && data.strains[key].length > 0
        );
      }
    }
    return [];
  };

  const availableStrains = getAvailableStrains();

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', mt: 0 }}>
      {/* Bridge Rendering Image */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: '62px',
        mb: 3,
        px: 2
      }}>
        <img 
          src="/new_bridge_rendering.png"
          alt="Brent Bridge Rendering" 
          style={{
            maxWidth: '100%',
            width: '90%',
            height: 'auto',
            maxHeight: '350px',
            borderRadius: '12px',
            boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
          }}
        />
      </Box>

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Multi-Pier Comparison
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography>Loading data...</Typography>
        </Box>
      )}

      {/* Controls */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3, 
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Pier Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Piers:</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[1, 2, 3].map(pier => (
              <FormControlLabel
                key={pier}
                control={
                  <Checkbox
                    checked={selectedPiers.includes(pier)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPiers([...selectedPiers, pier]);
                      } else {
                        setSelectedPiers(selectedPiers.filter(p => p !== pier));
                      }
                    }}
                    sx={{
                      color: pierColors[pier],
                      '&.Mui-checked': {
                        color: pierColors[pier],
                      },
                    }}
                  />
                }
                label={`Pier ${pier}`}
              />
            ))}
          </Box>
        </Box>

        {/* Data Type Selection */}
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Data Type</InputLabel>
          <Select
            value={selectedDataType}
            label="Data Type"
            onChange={(e) => setSelectedDataType(e.target.value)}
          >
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="strain">Strain</MenuItem>
          </Select>
        </FormControl>

        {/* Strain Selection (only show if strain is selected) */}
        {selectedDataType === 'strain' && availableStrains.length > 0 && (
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Strain Gauge</InputLabel>
            <Select
              value={selectedStrain}
              label="Strain Gauge"
              onChange={(e) => setSelectedStrain(e.target.value)}
            >
              {availableStrains.map(strain => (
                <MenuItem key={strain} value={strain}>{strain}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Data Status Chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {selectedPiers.map(pier => {
          const data = pierData[pier];
          if (data?.error) {
            return (
              <Chip 
                key={pier} 
                label={`Pier ${pier}: Error`} 
                color="error" 
                size="small"
              />
            );
          }
          const hasStatus = data?.status && data.status.length > 0;
          const hasStrain = data?.strains && Object.keys(data.strains).length > 0;
          return (
            <Chip 
              key={pier} 
              label={`Pier ${pier}: ${hasStatus ? `${data.status.length} status` : ''} ${hasStatus && hasStrain ? '|' : ''} ${hasStrain ? `${Object.keys(data.strains).length} strains` : ''}`}
              color="success" 
              size="small"
            />
          );
        })}
      </Box>

      {/* Plot */}
      {plotData.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <Plot {...plotConfig} />
        </Box>
      ) : (
        !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {selectedPiers.length === 0 
              ? 'Please select at least one pier to compare.'
              : 'No data available for the selected piers and data type.'}
          </Alert>
        )
      )}
    </Box>
  );
};

export default ComparePiers;
